import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { TabView, TabPanel } from 'primereact/tabview';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { CustomerSuggestionsTab } from '../components/customer-identification/CustomerSuggestionsTab';
import { VendorSuggestionsTab } from '../components/customer-identification/VendorSuggestionsTab';
import { ReviewChecksTab } from '../components/customer-identification/ReviewChecksTab';
import { UnassignedDepositsTab } from '../components/customer-identification/UnassignedDepositsTab';
import { CustomersTab } from '../components/customer-identification/CustomersTab';
import { VendorsTab } from '../components/customer-identification/VendorsTab';
import { customersApi, PotentialCustomer } from '../api/customers';
import { vendorsApi } from '../api/vendors';
import { checksApi } from '../api/checks';
import { depositsApi } from '../api/deposits';
import { Check, CustomerDeposit, PotentialVendor } from '../types';
import { useBackgroundJob, useClientJobs, triggerJobsRefresh } from '../hooks';

interface EditableCustomer extends PotentialCustomer {
  expanded?: boolean;
}

interface EditableVendor extends PotentialVendor {
  expanded?: boolean;
}

export const CustomerIdentification: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const toast = useRef<Toast>(null);

  // Customer state
  const [customers, setCustomers] = useState<EditableCustomer[]>([]);
  const [checks, setChecks] = useState<Check[]>([]);
  const [deposits, setDeposits] = useState<CustomerDeposit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSuggestionId, setSavingSuggestionId] = useState<string | null>(null);
  const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [identifyJobId, setIdentifyJobId] = useState<string | null>(null);
  const [isAcceptingAllCustomers, setIsAcceptingAllCustomers] = useState(false);

  // Vendor state
  const [vendors, setVendors] = useState<EditableVendor[]>([]);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [savingVendorId, setSavingVendorId] = useState<string | null>(null);
  const [showAddVendorDialog, setShowAddVendorDialog] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [isAcceptingAllVendors, setIsAcceptingAllVendors] = useState(false);

  // Background job tracking for customer identification
  const {
    status: jobStatus,
    progress: jobProgress,
    error: jobError,
    isComplete: jobComplete,
    isFailed: jobFailed,
  } = useBackgroundJob(identifyJobId);

  // Track active jobs for this client to disable buttons
  const { isJobTypeRunning } = useClientJobs(clientId);

  useEffect(() => {
    if (clientId) {
      loadInitial();
    }
  }, [clientId]);

  // Handle job completion or failure
  const refreshCustomers = useCallback(async () => {
    if (!clientId) return;
    try {
      const potential = await customersApi.getPotentialCustomers(clientId);
      const editable = potential.map((c) => ({
        ...c,
        expanded: false,
      }));
      setCustomers(editable);
    } catch (err: any) {
      setError(err?.message || 'Failed to refresh customers');
    }
  }, [clientId]);

  const refreshVendors = useCallback(async () => {
    if (!clientId) return;
    try {
      const potentialVendors = await vendorsApi.getVendorSuggestions(clientId);
      const editable = potentialVendors.map((v) => ({
        ...v,
        expanded: false,
      }));
      setVendors(editable);
    } catch (err: any) {
      setVendorError(err?.message || 'Failed to refresh vendors');
    }
  }, [clientId]);

  useEffect(() => {
    if (jobComplete) {
      // Job completed successfully - refresh both customers and vendors
      refreshCustomers();
      refreshVendors();
      setIdentifyJobId(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Entity identification completed',
        life: 3000,
      });
    } else if (jobFailed) {
      // Job failed
      setIdentifyJobId(null);
      setError(jobError || 'Entity identification failed');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: jobError || 'Entity identification failed',
        life: 5000,
      });
    }
  }, [jobComplete, jobFailed, jobError, refreshCustomers, refreshVendors]);

  const loadInitial = async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    setVendorError(null);
    try {
      const [potentialCustomers, potentialVendors, checkList, depositList] = await Promise.all([
        customersApi.getPotentialCustomers(clientId).catch(() => []),
        vendorsApi.getVendorSuggestions(clientId).catch(() => []),
        checksApi.listChecks(clientId).catch(() => []),
        depositsApi.listDeposits(clientId).catch(() => []),
      ]);
      const editableCustomers = potentialCustomers.map((c) => ({
        ...c,
        expanded: false,
      }));
      const editableVendors = potentialVendors.map((v) => ({
        ...v,
        expanded: false,
      }));
      setCustomers(editableCustomers);
      setVendors(editableVendors);
      setChecks(checkList);
      setDeposits(depositList);
    } catch (err: any) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleIdentifyCustomers = async () => {
    if (!clientId) return;

    // Don't start a new job if one is already running
    if (identifyJobId) {
      toast.current?.show({
        severity: 'info',
        summary: 'In Progress',
        detail: 'Customer identification is already running',
        life: 3000,
      });
      return;
    }

    setError(null);
    try {
      const { job_id } = await customersApi.identifyCustomers(clientId);
      setIdentifyJobId(job_id);
      triggerJobsRefresh(); // Immediately update the job indicator
      toast.current?.show({
        severity: 'info',
        summary: 'Started',
        detail: 'Customer identification started',
        life: 3000,
      });
    } catch (err: any) {
      if (err.response?.status === 404) {
        toast.current?.show({
          severity: 'info',
          summary: 'Not Available',
          detail: 'AI customer identification is not yet available',
          life: 3000,
        });
      } else if (err.response?.status === 409) {
        // Job already running for this client
        toast.current?.show({
          severity: 'warn',
          summary: 'Job In Progress',
          detail: 'Another job is already running for this client',
          life: 3000,
        });
      } else {
        setError(err?.message || 'Failed to start customer identification');
      }
    }
  };

  const handleAcceptCustomer = async (suggestionId: string | null) => {
    const customer = customers.find((c) => c.suggestion_id === suggestionId);
    if (!customer || !customer.suggestion_id) return;

    setSavingSuggestionId(customer.suggestion_id);
    try {
      await customersApi.acceptPotentialCustomer(customer.suggestion_id);

      // Update status to accepted
      setCustomers((prev) =>
        prev.map((c) =>
          c.suggestion_id === suggestionId ? { ...c, status: 'accepted' as const } : c
        )
      );

      // Reload checks and deposits to reflect the new assignment
      if (clientId) {
        const [checkList, depositList] = await Promise.all([
          checksApi.listChecks(clientId),
          depositsApi.listDeposits(clientId),
        ]);
        setChecks(checkList);
        setDeposits(depositList);
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer accepted',
        life: 3000,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to accept customer');
    } finally {
      setSavingSuggestionId(null);
    }
  };

  const handleRejectCustomer = async (suggestionId: string | null) => {
    if (!suggestionId) return;

    setSavingSuggestionId(suggestionId);
    try {
      await customersApi.rejectPotentialCustomer(suggestionId);
      // Update status to rejected
      setCustomers((prev) =>
        prev.map((c) =>
          c.suggestion_id === suggestionId ? { ...c, status: 'rejected' as const } : c
        )
      );
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer rejected',
        life: 3000,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to reject customer');
    } finally {
      setSavingSuggestionId(null);
    }
  };

  const handleRenameCustomer = async (suggestionId: string | null, name: string) => {
    if (!suggestionId) {
      // For unsaved customers, just update local state
      setCustomers((prev) =>
        prev.map((c) => (c.suggestion_id === suggestionId ? { ...c, client_name: name } : c))
      );
      return;
    }

    try {
      await customersApi.renamePotentialCustomer(suggestionId, name);
      setCustomers((prev) =>
        prev.map((c) => (c.suggestion_id === suggestionId ? { ...c, client_name: name } : c))
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to rename customer');
    }
  };

  const handleRemoveCheck = (suggestionId: string | null, checkId: string) => {
    if (!suggestionId || !checkId) return;

    const checksById = new Map<string, Check>();
    checks.forEach((c) => checksById.set(c.id, c));
    const check = checksById.get(checkId);
    const checkInfo = check
      ? `#${check.check_number || 'N/A'} (payee: ${check.payee_name || 'Unknown'})`
      : `Check ID ${checkId}`;

    confirmDialog({
      message: `Are you sure you want to remove Check ${checkInfo} from this customer?`,
      header: 'Confirm Removal',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await customersApi.removeCheckFromSuggestion(suggestionId, checkId);
          // Update local state after successful deletion
          setCustomers((prev) =>
            prev.map((c) =>
              c.suggestion_id === suggestionId
                ? { ...c, referenced_checks: c.referenced_checks.filter((id) => id !== checkId) }
                : c
            )
          );
        } catch (err: any) {
          setError(err?.message || 'Failed to remove check from suggestion');
        }
      },
    });
  };

  const handleRemoveDeposit = (suggestionId: string | null, depositId: string) => {
    if (!suggestionId || !depositId) return;

    const depositsById = new Map<string, CustomerDeposit>();
    deposits.forEach((d) => d.id && depositsById.set(d.id, d));
    const deposit = depositsById.get(depositId);
    const depositInfo = deposit
      ? `for ${deposit.customer_name || 'Unknown'} (${deposit.amount ? `$${deposit.amount})` : 'No amount'}`
      : `Deposit ID ${depositId}`;

    confirmDialog({
      message: `Are you sure you want to remove deposit ${depositInfo} from this customer?`,
      header: 'Confirm Removal',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await customersApi.removeDepositFromSuggestion(suggestionId, depositId);
          // Update local state after successful deletion
          setCustomers((prev) =>
            prev.map((c) =>
              c.suggestion_id === suggestionId
                ? { ...c, referenced_deposits: c.referenced_deposits.filter((id) => id !== depositId) }
                : c
            )
          );
        } catch (err: any) {
          setError(err?.message || 'Failed to remove deposit from suggestion');
        }
      },
    });
  };

  const handleAddCustomer = () => {
    setNewCustomerName('');
    setShowAddCustomerDialog(true);
  };

  const handleSaveNewCustomer = async () => {
    if (!clientId || !newCustomerName.trim()) {
      setError('Please enter a customer name');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await customersApi.createCustomer(clientId, { name: newCustomerName.trim() });
      setShowAddCustomerDialog(false);
      setNewCustomerName('');
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Customer created',
        life: 3000,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptAllCustomers = async () => {
    const pendingCustomers = customers.filter((c) => c.status === 'pending' && c.suggestion_id);
    if (pendingCustomers.length === 0) return;

    setIsAcceptingAllCustomers(true);
    setError(null);
    toast.current?.show({
      severity: 'info',
      summary: 'Processing',
      detail: `Accepting ${pendingCustomers.length} customers...`,
      life: 3000,
    });
    try {
      for (const customer of pendingCustomers) {
        await customersApi.acceptPotentialCustomer(customer.suggestion_id!);
      }
      // Update all to accepted status
      setCustomers((prev) =>
        prev.map((c) =>
          c.status === 'pending' && c.suggestion_id ? { ...c, status: 'accepted' as const } : c
        )
      );
      // Reload checks and deposits
      if (clientId) {
        const [checkList, depositList] = await Promise.all([
          checksApi.listChecks(clientId),
          depositsApi.listDeposits(clientId),
        ]);
        setChecks(checkList);
        setDeposits(depositList);
      }
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Accepted ${pendingCustomers.length} customers`,
        life: 3000,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to accept all customers');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.message || 'Failed to accept all customers',
        life: 5000,
      });
    } finally {
      setIsAcceptingAllCustomers(false);
    }
  };

  // Vendor handlers
  const handleAcceptVendor = async (suggestionId: string) => {
    if (!suggestionId) return;

    setSavingVendorId(suggestionId);
    try {
      await vendorsApi.acceptVendor(suggestionId);
      setVendors((prev) =>
        prev.map((v) =>
          v.suggestion_id === suggestionId ? { ...v, status: 'accepted' as const } : v
        )
      );

      // Reload checks to reflect the new assignment
      if (clientId) {
        const checkList = await checksApi.listChecks(clientId);
        setChecks(checkList);
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Vendor accepted',
        life: 3000,
      });
    } catch (err: any) {
      setVendorError(err?.message || 'Failed to accept vendor');
    } finally {
      setSavingVendorId(null);
    }
  };

  const handleRejectVendor = async (suggestionId: string) => {
    if (!suggestionId) return;

    setSavingVendorId(suggestionId);
    try {
      await vendorsApi.rejectVendor(suggestionId);
      setVendors((prev) =>
        prev.map((v) =>
          v.suggestion_id === suggestionId ? { ...v, status: 'rejected' as const } : v
        )
      );
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Vendor rejected',
        life: 3000,
      });
    } catch (err: any) {
      setVendorError(err?.message || 'Failed to reject vendor');
    } finally {
      setSavingVendorId(null);
    }
  };

  const handleRenameVendor = async (suggestionId: string, name: string) => {
    if (!suggestionId) return;

    try {
      await vendorsApi.renameVendor(suggestionId, name);
      setVendors((prev) =>
        prev.map((v) => (v.suggestion_id === suggestionId ? { ...v, vendor_name: name } : v))
      );
    } catch (err: any) {
      setVendorError(err?.message || 'Failed to rename vendor');
    }
  };

  const handleRemoveCheckFromVendor = (suggestionId: string, checkId: string) => {
    if (!suggestionId || !checkId) return;

    const checksById = new Map<string, Check>();
    checks.forEach((c) => checksById.set(c.id, c));
    const check = checksById.get(checkId);
    const checkInfo = check
      ? `#${check.check_number || 'N/A'} (payee: ${check.payee_name || 'Unknown'})`
      : `Check ID ${checkId}`;

    confirmDialog({
      message: `Are you sure you want to remove Check ${checkInfo} from this vendor?`,
      header: 'Confirm Removal',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await vendorsApi.removeCheckFromVendor(suggestionId, checkId);
          setVendors((prev) =>
            prev.map((v) =>
              v.suggestion_id === suggestionId
                ? { ...v, referenced_checks: v.referenced_checks.filter((id) => id !== checkId) }
                : v
            )
          );
        } catch (err: any) {
          setVendorError(err?.message || 'Failed to remove check from vendor');
        }
      },
    });
  };

  const handleAddVendor = () => {
    setNewVendorName('');
    setShowAddVendorDialog(true);
  };

  const handleIdentifyVendors = () => {
    // Vendors are identified together with customers via the same job
    handleIdentifyCustomers();
  };

  const handleAcceptAllVendors = async () => {
    const pendingVendors = vendors.filter((v) => v.status === 'pending' && v.suggestion_id);
    if (pendingVendors.length === 0) return;

    setIsAcceptingAllVendors(true);
    setVendorError(null);
    toast.current?.show({
      severity: 'info',
      summary: 'Processing',
      detail: `Accepting ${pendingVendors.length} vendors...`,
      life: 3000,
    });
    try {
      for (const vendor of pendingVendors) {
        await vendorsApi.acceptVendor(vendor.suggestion_id);
      }
      // Update all to accepted status
      setVendors((prev) =>
        prev.map((v) =>
          v.status === 'pending' && v.suggestion_id ? { ...v, status: 'accepted' as const } : v
        )
      );
      // Reload checks
      if (clientId) {
        const checkList = await checksApi.listChecks(clientId);
        setChecks(checkList);
      }
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Accepted ${pendingVendors.length} vendors`,
        life: 3000,
      });
    } catch (err: any) {
      setVendorError(err?.message || 'Failed to accept all vendors');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.message || 'Failed to accept all vendors',
        life: 5000,
      });
    } finally {
      setIsAcceptingAllVendors(false);
    }
  };

  if (!clientId) {
    return (
      <>
        <Message severity="error" text="Client ID is required" />
      </>
    );
  }

  return (
    <>
      <Toast ref={toast} />

      <TabView>
        <TabPanel header="Customer Suggestions">
          <CustomerSuggestionsTab
            customers={customers}
            checks={checks}
            deposits={deposits}
            loading={loading}
            savingSuggestionId={savingSuggestionId}
            error={error}
            jobStatus={jobStatus}
            jobProgress={jobProgress}
            isIdentifyJobRunning={isJobTypeRunning('identify_customers')}
            onIdentifyCustomers={handleIdentifyCustomers}
            onAddCustomer={handleAddCustomer}
            onAcceptCustomer={handleAcceptCustomer}
            onRejectCustomer={handleRejectCustomer}
            onRenameCustomer={handleRenameCustomer}
            onRemoveCheck={handleRemoveCheck}
            onRemoveDeposit={handleRemoveDeposit}
            onAcceptAll={handleAcceptAllCustomers}
            isAcceptingAll={isAcceptingAllCustomers}
          />
        </TabPanel>

        <TabPanel header="Vendor Suggestions">
          <VendorSuggestionsTab
            vendors={vendors}
            checks={checks}
            loading={loading}
            savingSuggestionId={savingVendorId}
            error={vendorError}
            jobStatus={jobStatus}
            jobProgress={jobProgress}
            isIdentifyJobRunning={isJobTypeRunning('identify_customers')}
            onIdentifyVendors={handleIdentifyVendors}
            onAddVendor={handleAddVendor}
            onAcceptVendor={handleAcceptVendor}
            onRejectVendor={handleRejectVendor}
            onRenameVendor={handleRenameVendor}
            onRemoveCheck={handleRemoveCheckFromVendor}
            onAcceptAll={handleAcceptAllVendors}
            isAcceptingAll={isAcceptingAllVendors}
          />
        </TabPanel>

        <TabPanel header="Review Checks">
          <ReviewChecksTab clientId={clientId} />
        </TabPanel>

        <TabPanel header="Review Deposits">
          <UnassignedDepositsTab clientId={clientId} />
        </TabPanel>

        <TabPanel header="Customers">
          <CustomersTab clientId={clientId} />
        </TabPanel>

        <TabPanel header="Vendors">
          <VendorsTab clientId={clientId} />
        </TabPanel>
      </TabView>

      <Dialog
        header="Add New Customer"
        visible={showAddCustomerDialog}
        onHide={() => setShowAddCustomerDialog(false)}
        className="customer-create-dialog"
      >
        <div>
          <div className="customer-create-field">
            <label htmlFor="new-customer-name" className="customer-create-label">
              Customer Name
            </label>
            <InputText
              id="new-customer-name"
              value={newCustomerName}
              onChange={(e) => setNewCustomerName(e.target.value)}
              placeholder="Enter customer name"
              className="customer-create-input"
              autoFocus
            />
          </div>

          <div className="allocation-dialog-footer">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={() => setShowAddCustomerDialog(false)}
              disabled={saving}
            />
            <Button
              label="Create"
              onClick={handleSaveNewCustomer}
              disabled={saving || !newCustomerName.trim()}
            />
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Add New Vendor"
        visible={showAddVendorDialog}
        onHide={() => setShowAddVendorDialog(false)}
        className="customer-create-dialog"
      >
        <div>
          <div className="customer-create-field">
            <label htmlFor="new-vendor-name" className="customer-create-label">
              Vendor Name
            </label>
            <InputText
              id="new-vendor-name"
              value={newVendorName}
              onChange={(e) => setNewVendorName(e.target.value)}
              placeholder="Enter vendor name"
              className="customer-create-input"
              autoFocus
            />
          </div>

          <div className="allocation-dialog-footer">
            <Button
              label="Cancel"
              severity="secondary"
              onClick={() => setShowAddVendorDialog(false)}
              disabled={saving}
            />
            <Button
              label="Create"
              onClick={() => {
                // TODO: Implement vendor creation API when available
                setShowAddVendorDialog(false);
                toast.current?.show({
                  severity: 'info',
                  summary: 'Not Implemented',
                  detail: 'Vendor creation is not yet implemented',
                  life: 3000,
                });
              }}
              disabled={saving || !newVendorName.trim()}
            />
          </div>
        </div>
      </Dialog>

      <ConfirmDialog />
    </>
  );
};

export default CustomerIdentification;
